import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import './msoStatus.css';

import { getMsoStatusBulk } from '../../lib/api';
import { TrackScore } from '../SharedComponents';

/**
 * MSO status for the Documents grid.
 *
 * Renders as a score button matching the Doc / Wave / IP columns, so the row
 * reads as one set of comparable numbers. Non-scored states (running, not
 * run, missing sources) render as compact text instead.
 *
 * Usage:
 *   <MSOStatusProvider caseIds={ids}> ... </MSOStatusProvider>
 *   <MSOStatusCell caseId={group.pid} onOpen={id => nav(...)} />
 *
 * One bulk request covers the whole page rather than one call per row.
 */

const MSOStatusContext = createContext({ statuses: {}, loading: false, refresh: () => {} });

export function MSOStatusProvider({ caseIds = [], children, pollWhileRunning = true }) {
  const [statuses, setStatuses] = useState({});
  const [loading, setLoading] = useState(false);

  const key = caseIds.join(',');

  const refresh = useCallback(async () => {
    const ids = key ? key.split(',').filter(Boolean) : [];
    if (ids.length === 0) { setStatuses({}); return; }
    setLoading(true);
    try {
      const result = await getMsoStatusBulk(ids);
      setStatuses(result || {});
    } catch {
      // A status column must never break the grid.
    } finally {
      setLoading(false);
    }
  }, [key]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    if (!pollWhileRunning) return undefined;
    const anyRunning = Object.values(statuses).some(s => s?.status === 'running');
    if (!anyRunning) return undefined;
    const timer = setInterval(refresh, 15000);
    return () => clearInterval(timer);
  }, [statuses, refresh, pollWhileRunning]);

  return (
    <MSOStatusContext.Provider value={{ statuses, loading, refresh }}>
      {children}
    </MSOStatusContext.Provider>
  );
}

export function useMSOStatus(caseId) {
  const { statuses, refresh } = useContext(MSOStatusContext);
  return { status: statuses[String(caseId)] || null, refresh };
}

export function MSOStatusCell({ caseId, onOpen }) {
  const { status } = useMSOStatus(caseId);
  const state = status?.status || 'none';
  const score = status?.score;
  const findings = status?.discrepancyCount;

  // States with no number to show.
  if (state === 'running') {
    return <span className="mso-mini running">Running</span>;
  }
  if (state === 'unavailable') {
    return <span className="mso-mini" title={status?.headline || 'Missing source files'}>No Scrn</span>;
  }
  if (state === 'error') {
    return <span className="mso-mini poor" title={status?.headline || 'Run failed'}>Failed</span>;
  }
  if (state === 'none' || typeof score !== 'number') {
    return <TrackScore score={null} done={false} na={true} />;
  }

  const signed = state === 'approved' || state === 'rejected';

  const title = state === 'approved'
    ? 'Signed off'
    : state === 'rejected'
      ? 'Rejected'
      : (typeof findings === 'number' && findings > 0
          ? findings + (findings === 1 ? ' finding' : ' findings')
          : 'Ready for review');

  const body = <TrackScore score={score} done={signed} />;

  if (!onOpen) return body;

  return (
    <button className="mso-score-btn" title={title} onClick={() => onOpen(caseId)}>
      {body}
    </button>
  );
}

export default MSOStatusCell;